import { ApiError } from '@/shared/data/client';
import {
  authUamErrorKinds,
  getAuthUamErrorKindFromDetails,
  type AuthUamErrorKind,
} from '@flowqa/shared';

type ClerkErrorEntry = {
  code?: string;
};

const normalizeMessage = (value: string): string => value.trim().toLowerCase();

const getApiAuthUamErrorKind = (error: unknown): AuthUamErrorKind | null => {
  return error instanceof ApiError ? getAuthUamErrorKindFromDetails(error.details) : null;
};

const hasClerkErrorCode = (error: unknown, code: string): boolean => {
  if (typeof error !== 'object' || error === null || !('errors' in error)) {
    return false;
  }
  const errors = (error as { errors?: ClerkErrorEntry[] }).errors;
  return Array.isArray(errors) && errors.some((entry) => entry.code === code);
};

export const resolveAuthUamErrorKind = (input: {
  error: unknown;
  message?: string;
}): AuthUamErrorKind | null => {
  const apiKind = getApiAuthUamErrorKind(input.error);
  if (apiKind) {
    return apiKind;
  }

  if (
    hasClerkErrorCode(input.error, 'form_identifier_not_found') ||
    hasClerkErrorCode(input.error, 'form_param_nil')
  ) {
    return authUamErrorKinds.accountNotFound;
  }

  if (!(typeof input.message === 'string' && input.message.length > 0)) {
    return null;
  }

  const normalized = normalizeMessage(input.message);
  if (input.error instanceof ApiError) {
    if (input.error.code === 'AUTH_FAILED' && normalized.includes('identity mismatch')) {
      return authUamErrorKinds.identityMismatch;
    }
    if (
      (input.error.code === 'AUTH_FAILED' || input.error.status === 401) &&
      (normalized.includes('session token expired') || normalized.includes('jwt expired'))
    ) {
      return authUamErrorKinds.sessionExpired;
    }
  }

  if (
    normalized.includes("couldn't find your account") ||
    normalized.includes('could not find your account') ||
    normalized.includes("couldn't find an account with that email") ||
    normalized.includes('could not find an account with that email') ||
    normalized.includes('account not found') ||
    normalized.includes('missing user identity') ||
    normalized.includes('user not found')
  ) {
    return authUamErrorKinds.accountNotFound;
  }

  if (
    normalized.includes('clerk membership is still provisioning') ||
    normalized.includes('workspace access is still being set up')
  ) {
    return authUamErrorKinds.inviteMembershipNotReady;
  }

  return null;
};

export const isAuthUamErrorKindMatch = (input: {
  error: unknown;
  expectedKind: AuthUamErrorKind;
  message?: string;
}): boolean => resolveAuthUamErrorKind(input) === input.expectedKind;
